import { infiniteQueryOptions, queryOptions, skipToken } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { FIRST_PAGE, getNextPageFromMeta } from '@/api/pagination';
import { STALE_TIME } from '@/api/staleTimes';
import { DEFAULT_INSTITUTE_PAGE_SIZE } from '../constants/institute.constants';
import {
  instituteDetailSchema,
  paginatedInstitutesSchema,
  type CreateInstitutePayload,
  type InstituteDetail,
  type InstituteListParams,
  type PaginatedInstitutes,
  type UpdateInstitutePayload,
} from '../types/institute.types';

export const instituteQueryKeys = {
  all: () => ['institutes'] as const,
  lists: () => [...instituteQueryKeys.all(), 'list'] as const,
  listInfinite: (params: InstituteListParams) =>
    [...instituteQueryKeys.lists(), 'infinite', params] as const,
  listDisabled: () => [...instituteQueryKeys.lists(), 'disabled'] as const,
  details: () => [...instituteQueryKeys.all(), 'detail'] as const,
  detail: (id: number) => [...instituteQueryKeys.details(), id] as const,
};

export const instituteListInfiniteQueryOptions = (params: InstituteListParams | null) =>
  infiniteQueryOptions({
    queryKey: params ? instituteQueryKeys.listInfinite(params) : instituteQueryKeys.listDisabled(),
    queryFn: params
      ? async ({ pageParam }): Promise<PaginatedInstitutes> => {
          const res = await apiClient.get(API.institute.list, {
            params: { ...params, page: pageParam, limit: DEFAULT_INSTITUTE_PAGE_SIZE },
          });
          return paginatedInstitutesSchema.parse(res.data);
        }
      : skipToken,
    initialPageParam: FIRST_PAGE,
    getNextPageParam: getNextPageFromMeta,
    staleTime: STALE_TIME,
  });

export const instituteDetailQueryOptions = (id: number) =>
  queryOptions({
    queryKey: instituteQueryKeys.detail(id),
    queryFn: async (): Promise<InstituteDetail> => {
      const res = await apiClient.get(API.institute.detail(id));
      return instituteDetailSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export async function createInstitute(payload: CreateInstitutePayload): Promise<void> {
  await apiClient.post(API.institute.create, payload);
}

export async function updateInstitute(id: number, payload: UpdateInstitutePayload): Promise<void> {
  await apiClient.patch(API.institute.update(id), payload);
}

export async function deleteInstitute(id: number): Promise<void> {
  await apiClient.delete(API.institute.delete(id));
}
